import React, { useCallback, useRef, useState, useMemo } from 'react';
import { mergeI18n } from '@bingads-webui-campaign-react/i18n-merger';
import _ from 'underscore';
import classNames from 'classnames';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Button,
  mergeClasses,
} from '@fluentui/react-components';
import { MessageBar, MessageBarActions, MessageBarBody } from '@fluentui/react-message-bar';
import { DismissRegular } from '@fluentui/react-icons';
import { ChooseImageIcon, supportedImageExtensions, useUploadFiles, AdsStudioSpinner, isReachFileSizeLimit, getReachFileSizeLimitErrorMessage, } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { parseFileName } from '@bingads-webui-campaign-react/upload-file-picker';
import { BackgroundRemovalLibraryGrid } from './library-grid';
import { ViewName } from '../edit-image-page-component/model/utils';
import { useBackgroundRemovalModalStyle } from './background-removal-modal-style';

const UploadArea = ({
  i18n, onFileRead, setErrors,
}) => {
  const classes = useBackgroundRemovalModalStyle();
  const uploadInput = useRef();
  /* istanbul ignore next */
  const onClickUpload = useCallback(() => {
    uploadInput.current.click();
  }, []);

  const clearInputFile = useCallback((event) => {
    if (event) {
      // eslint-disable-next-line no-param-reassign
      event.target.value = '';
    }
  }, []);

  const readFile = useCallback((fileObjects, event) => {
    const selectedFiles = !_.isUndefined(fileObjects) && fileObjects.length > 0 && fileObjects;
    if (selectedFiles) {
      const selectedFilesCount = selectedFiles.length;
      let fileIndex;
      const errorArr = [];

      for (fileIndex = 0; fileIndex < selectedFilesCount; fileIndex += 1) {
        const fileObj = selectedFiles[fileIndex];
        const fileSizeMB = (fileObj.size / 1024 / 1024).toFixed(3);
        let reachLimit;

        if (isReachFileSizeLimit) {
          const { ext } = parseFileName(fileObj.name);
          reachLimit = isReachFileSizeLimit({ ext, fileSizeMB });
        }

        if (reachLimit) {
          errorArr.push(getReachFileSizeLimitErrorMessage({ i18n }));

          break;
        }
      }
      if (_.isEmpty(errorArr)) {
        return onFileRead(selectedFiles, () => clearInputFile(event));
      }
      clearInputFile(event);
      setErrors(errorArr);
    } // else no media picked, so do nothing

    return ''; // for testability
  }, [clearInputFile, i18n, onFileRead, setErrors]);

  const handleUploadFile = useCallback((event) => {
    const { files } = event.target;
    readFile(files, event);
  }, [readFile]);

  const handleDropFile = useCallback((event) => {
    event.preventDefault();
    const droppedFiles = event.dataTransfer.files;
    readFile(droppedFiles);
  }, [readFile]);

  const handleDropOver = useCallback((event) => {
    event.preventDefault();
  }, []);

  return (
    <>
      <div className={classes.uploadContainer}>
        <Button className={mergeClasses('choose-image-container', classes.chooseImageContainer)} onClick={onClickUpload} onDrop={handleDropFile} onDragOver={handleDropOver}>
          <img className={classes.uploadIcon} src={ChooseImageIcon} alt={i18n.getString(_TL_('Choose an image'))} />
          <div>
            <div className={classes.uploadTitle}>
              {i18n.getString(_TL_('Upload or drag your assets here'))}
              <i className={classes.browse}>{i18n.getString(_TL_('Browse'))}</i>
            </div>
          </div>
        </Button>
        <div className={classes.uploadSubtext}>
          {i18n.getString(_TL_('Minimum image size: 703 x 368 pixels.'))}
          <button title={i18n.getString(_TL_('Learn more about asset guidelines and accepted file types.'))} className={classNames(`HelpTopic ${classes.helpLink}`)} query="help:app54863">{i18n.getString(_TL_('Learn more about asset guidelines and accepted file types.'))}</button>
        </div>
      </div>
      <input
        ref={uploadInput}
        tabIndex={-1}
        aria-label={i18n.getString(_TL_('upload'))}
        className={mergeClasses('upload-input', classes.uploadButton)}
        onChange={handleUploadFile}
        type="file"
        multiple={false}
        accept={supportedImageExtensions.join(',')}
      />
    </>
  );
};

const LoadingComp = () => {
  const classes = useBackgroundRemovalModalStyle();

  return (
    <div className={classes.loadingMask}>
      <AdsStudioSpinner useCustomSize customStyle={{ marginTop: '250px' }} />
    </div>
  );
};


const BackgroundRemovalModal = ({
  navigation, i18n, dataService, logger, notificationApi, aid, globalLegacyI18n, open, isUploadImageUpscaleEnabled,
}) => {
  const [errors, setErrors] = useState([]);
  const [selectedImage, setSelectedImage] = useState();
  const globalI18n = useMemo(() => globalLegacyI18n.get('$/i18n!legacy/global'), [globalLegacyI18n]);
  const classes = useBackgroundRemovalModalStyle();

  const filePickerI18n = useMemo(() => mergeI18n(globalI18n, i18n), [globalI18n, i18n]);
  /* istanbul ignore next */
  const onFilesAddedCallback = useCallback(() => {
    setErrors([]);
  }, []);
  /* istanbul ignore next */
  const uploadCallback = useCallback((imageId) => {
    if (imageId) {
      navigation.goBack();
      setSelectedImage(null);
      if (logger && logger.logTrace) {
        logger.logTrace('Image Details: edit image', { assetId: imageId });
      }
      navigation.gotoEditImagePage({
        imageId, initialCurrentView: ViewName.BackgroundChangeView,
      });
    }
  }, [logger, navigation]);

  const onClickCancel = useCallback(() => {
    navigation.goBack();
    setSelectedImage(null);
    setErrors([]);
  }, [navigation]);

  const onSelectedImageChange = useCallback((newSelectedImage) => {
    setSelectedImage(newSelectedImage);
  }, []);

  const onClickContinue = useCallback(() => {
    navigation.goBack();
    setSelectedImage(null);
    if (logger && logger.logTrace) {
      logger.logTrace('Image Details: edit image', { assetId: selectedImage.id });
    }
    navigation.gotoEditImagePage({
      imageId: selectedImage.id, initialCurrentView: ViewName.BackgroundChangeView,
    });
  }, [logger, navigation, selectedImage]);

  const {
    previewArr, onFileRead,
  } = useUploadFiles({
    dataService, logger, notificationApi, i18n, aid, onFilesAddedCallback, setErrors, filePickerI18n, uploadCallback, isUploadImageUpscaleEnabled,
  });

  const isUploadingFile = useMemo(() => !!(previewArr && previewArr.length), [previewArr]);
  const dialogContentRef = useRef();
  const isContinueButtonDisabled = useMemo(() => !selectedImage || isUploadingFile, [isUploadingFile, selectedImage]);

  return (
    <Dialog open={open}>
      <DialogSurface>
        <DialogBody className="background-removal-dialog">
          <DialogTitle>
            {i18n.getString(_TL_('Choose an image'))}
          </DialogTitle>
          <DialogContent className={mergeClasses('content-area', classes.contentArea)} ref={dialogContentRef}>
            <UploadArea setErrors={setErrors} i18n={i18n} onFileRead={onFileRead} isReachFileSizeLimit={isReachFileSizeLimit} getReachFileSizeLimitErrorMessage={getReachFileSizeLimitErrorMessage} />
            {
              errors.length > 0 &&
              errors.map((errMsg, index) => (
                <MessageBar
                  className={mergeClasses('error', classes.alertError)}
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${index}`}
                  intent="warning"
                >
                  <MessageBarBody>
                    {errMsg}
                  </MessageBarBody>
                  <MessageBarActions
                    containerAction={
                      <Button
                        onClick={() => setErrors([])}
                        aria-label="dismiss"
                        appearance="transparent"
                        icon={<DismissRegular />}
                      />
                    }
                  />
                </MessageBar>
              ))
            }
            { isUploadingFile && <LoadingComp /> }
            <BackgroundRemovalLibraryGrid contentRef={dialogContentRef} i18n={i18n} dataService={dataService} notificationApi={notificationApi} onSelectedImageChange={onSelectedImageChange} />
          </DialogContent>
          <DialogActions>
            <Button className={isContinueButtonDisabled ? 'disabled-continue' : 'done'} disabled={isContinueButtonDisabled} appearance="primary" onClick={onClickContinue}>{i18n.getString(_TL_('Add'))}</Button>
            <Button className="cancel" appearance="secondary" onClick={onClickCancel}>{i18n.getString(_TL_('Cancel'))}</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export const BackgroundRemovalModalComponent = BackgroundRemovalModal;
